<template>
  <div ref="root" class="sidebar-user">
    <div v-if="open" class="sidebar-user__menu" role="menu">
      <a href="#" class="sidebar-user__item" role="menuitem" @click.prevent="goToProfile">
        <i class="tim-icons icon-single-02"></i>My profile
      </a>
      <a href="#" class="sidebar-user__item" role="menuitem" @click.prevent="logout">
        <i class="tim-icons icon-button-power"></i>Sign out
      </a>
    </div>
    <button
      type="button"
      class="sidebar-user__button"
      :aria-expanded="open ? 'true' : 'false'"
      aria-haspopup="menu"
      :title="fullName"
      @click="open = !open"
    >
      <span class="sidebar-user__avatar">
        <img v-if="img" :src="img" alt="" />
        <template v-else>{{ initials }}</template>
      </span>
      <span class="sidebar-user__text">
        <span class="sidebar-user__name">{{ fullName }}</span>
        <span class="sidebar-user__email">{{ email }}</span>
      </span>
      <i class="tim-icons icon-minimal-up sidebar-user__chevron"></i>
    </button>
  </div>
</template>

<script lang="ts">
export default { name: 'sidebar-user-menu' }
</script>

<script setup lang="ts">
// Signed-in user card at the bottom of the sidebar (port of the otc-admin user menu).
// Opens My profile and Sign out; sign out goes through the shared helper so it keeps the
// same device preferences as the navbar's phone row.
import { getCurrentInstance, onBeforeUnmount, onMounted, ref } from 'vue'
import { signOut } from '@/util/signOut'

const proxy = getCurrentInstance()!.proxy as any
const PLACEHOLDER_IMG = 'https://console.offthecouch.io/img/placeholder.jpg'

function readProfile(): any {
  try {
    return JSON.parse(localStorage.getItem('profile') || '{}') || {}
  } catch (e) {
    return {}
  }
}

const profile = readProfile()
const fullName: string = [profile.first_name, profile.last_name].filter(Boolean).join(' ')
const email: string = profile.email || ''
const img: string = profile.img && profile.img !== PLACEHOLDER_IMG ? profile.img : ''
const initials: string = (
  [profile.first_name, profile.last_name].filter(Boolean).map((n: string) => n.charAt(0)).join('') ||
  email.charAt(0) ||
  '?'
).toUpperCase()

const root = ref<HTMLElement | null>(null)
const open = ref(false)

function goToProfile(): void {
  open.value = false
  proxy.$router.push('/settings/profile')
}

async function logout(): Promise<void> {
  open.value = false
  await signOut(proxy.$router, proxy.$route.fullPath)
}

function onDocMouseDown(e: MouseEvent): void {
  if (open.value && root.value && !root.value.contains(e.target as Node)) open.value = false
}

function onDocKey(e: KeyboardEvent): void {
  if (e.key === 'Escape' && open.value) open.value = false
}

onMounted(() => {
  document.addEventListener('mousedown', onDocMouseDown)
  document.addEventListener('keydown', onDocKey)
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDocMouseDown)
  document.removeEventListener('keydown', onDocKey)
})
</script>

<style lang="scss" scoped>
// Same rules as the otc-admin user menu; no global primitive covers a sidebar user card.
.sidebar-user__button {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}
.sidebar-user__avatar {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #344675;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  overflow: hidden;
}
.sidebar-user__avatar img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}
.sidebar-user__text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  transition: opacity 0.15s ease;
}
.sidebar-user__name {
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sidebar-user__email {
  font-size: 12px;
  line-height: 15px;
  opacity: 0.6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sidebar-user__chevron {
  font-size: 11px;
  opacity: 0.6;
  transition: opacity 0.15s ease;
}
.sidebar-user__menu {
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: calc(100% + 6px);
  padding: 4px;
  border-radius: 8px;
  background: #22233a;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.45);
  z-index: 5;
}
.sidebar-user__item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 6px;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  &:hover {
    background: rgba(125, 125, 125, 0.14);
    color: #fff;
  }
}
:global(.white-content) .sidebar-user__menu {
  background: #fff;
  border-color: rgba(29, 37, 59, 0.1);
  box-shadow: 0 16px 48px rgba(29, 37, 59, 0.18);
}
:global(.white-content) .sidebar-user__item,
:global(.white-content) .sidebar-user__item:hover {
  color: #1d253b;
}
</style>
